import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'

// Matches the live `invoices` table exactly -- see project_charges_phase1 memory / PR #41's
// description for how this diverged from supabase/init_schema.sql's version, which was never
// actually applied. `invoice_no` (not `invoice_number`, which nothing writes to meaningfully) is
// the real required number column; `status` is DRAFT/ISSUED/VOID (added in Phase 1), not the
// SENT/PAID/OVERDUE/CANCELLED payment-tracking lifecycle this file used to assume -- there is no
// `subtotal`/`exchange_rate`/`payment_terms`/`updated_at` column on this table. `notes` was added
// 2026-09-28 (20260928e_invoice_notes.sql) -- purely descriptive, editable regardless of status.
export interface Invoice {
  id: string
  invoice_no: string
  invoice_type?: string | null
  invoice_category?: string | null
  job_id?: string | null
  customer_id: string
  invoice_date: string
  due_date?: string | null
  notes?: string | null
  currency: string
  amount: number
  vat_amount: number
  total_amount: number
  status: 'DRAFT' | 'ISSUED' | 'VOID' | 'CREDITED'
  branch?: string | null
  is_posted?: boolean
  department?: string | null
  created_by: string
  created_at: string
  sage_invoice_id?: string | null
  sage_last_synced_at?: string | null
  voided_at?: string | null
  voided_by?: string | null
  void_reason?: string | null
  credit_note_of_invoice_id?: string | null
  credit_reason?: string | null
  customer?: {
    id: string
    code: string
    name: string
    contact_email?: string
  }
  job?: {
    id: string
    job_ref: string
    client_ref?: string
  }
}

export interface InvoiceFilters {
  status?: string
  customer_id?: string
  date_from?: string
  date_to?: string
  search?: string
}

export function useInvoices(filters?: InvoiceFilters) {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchInvoices = async () => {
    try {
      setLoading(true)
      setError(null)

      let query = supabase
        .from('invoices')
        .select(`
          *,
          customer:customers(id, code, name, contact_email),
          job:jobs(id, job_ref, client_ref)
        `)
        .order('created_at', { ascending: false })

      if (filters?.status) {
        query = query.eq('status', filters.status)
      }

      if (filters?.customer_id) {
        query = query.eq('customer_id', filters.customer_id)
      }

      if (filters?.date_from) {
        query = query.gte('invoice_date', filters.date_from)
      }

      if (filters?.date_to) {
        query = query.lte('invoice_date', filters.date_to)
      }

      if (filters?.search) {
        query = query.or(`invoice_no.ilike.%${filters.search}%,job.job_ref.ilike.%${filters.search}%`)
      }

      const { data, error } = await query

      if (error) throw error
      setInvoices(data || [])
    } catch (err) {
      console.error('Error fetching invoices:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch invoices')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchInvoices()
  }, [filters])

  const updateInvoice = async (id: string, updates: Partial<Invoice>) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      // Refresh invoices list
      await fetchInvoices()
      return data
    } catch (err) {
      console.error('Error updating invoice:', err)
      throw err
    }
  }

  return {
    invoices,
    loading,
    error,
    fetchInvoices,
    updateInvoice,
  }
}
